import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  buildCalibrationCalendar,
  generateCalibrationLayoutFromProfile,
  markSessionCompleted,
  rolloverMissedSession,
  type CalibrationCalendarLayout,
  type CalibrationCalendarPersisted,
  type CalibrationDayEntry,
  type CalibrationProfileInput,
  type CalibrationSessionSlot,
} from './calendarLayout'
import { isCalibrationComplete } from '../calibrationTypes'

const STORAGE_PREFIX = 'kilo.calibration.calendar'

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}.${userId}`
}

function readPersisted(userId: string | null): CalibrationCalendarPersisted | null {
  if (!userId || typeof sessionStorage === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(storageKey(userId))
    if (!raw) return null
    return JSON.parse(raw) as CalibrationCalendarPersisted
  } catch {
    return null
  }
}

function writePersisted(userId: string | null, state: CalibrationCalendarPersisted): void {
  if (!userId || typeof sessionStorage === 'undefined') return
  sessionStorage.setItem(storageKey(userId), JSON.stringify(state))
}

function toPersisted(layout: CalibrationCalendarLayout): CalibrationCalendarPersisted {
  return {
    phaseStartDate: layout.phaseStartDate,
    sessions: layout.sessions,
    completedSessionNumbers: layout.sessions
      .filter((s) => s.status === 'completed')
      .map((s) => s.sessionNumber),
  }
}

export type UseCalibrationCalendarOptions = {
  userId: string | null
  profile: CalibrationProfileInput | null
  /** ISO date when onboarding finished; defaults to today. */
  onboardingDate?: string
  /** When true, skip layout generation (production engine active). */
  disabled?: boolean
}

export type UseCalibrationCalendarResult = {
  layout: CalibrationCalendarLayout | null
  entries: CalibrationDayEntry[]
  sessions: CalibrationSessionSlot[]
  isActive: boolean
  isComplete: boolean
  nextPendingSession: CalibrationSessionSlot | null
  /** Entry for a specific ISO date, if within the phase window. */
  entryForDate: (isoDate: string) => CalibrationDayEntry | null
  /** Mark a session complete on the given date (updates sequence state). */
  completeSession: (sessionNumber: 1 | 2 | 3 | 4, completedDate: string) => void
  /** Roll the session scheduled on `missedDate` to the next training slot. */
  reportMissedDay: (missedDate: string) => void
  /** Rebuild from profile (e.g. after profile refresh). */
  refresh: () => void
}

export function useCalibrationCalendar({
  userId,
  profile,
  onboardingDate,
  disabled = false,
}: UseCalibrationCalendarOptions): UseCalibrationCalendarResult {
  const [sessions, setSessions] = useState<CalibrationSessionSlot[] | null>(null)
  const [phaseStartDate, setPhaseStartDate] = useState<string | null>(null)

  const hydrate = useCallback(() => {
    if (!profile || disabled || isCalibrationComplete(profile)) {
      setSessions(null)
      setPhaseStartDate(null)
      return
    }

    const persisted = readPersisted(userId)
    const layout = generateCalibrationLayoutFromProfile(
      profile,
      onboardingDate,
      persisted,
    )
    setSessions(layout.sessions)
    setPhaseStartDate(layout.phaseStartDate)
  }, [profile, disabled, userId, onboardingDate])

  useEffect(() => {
    hydrate()
  }, [hydrate])

  const layout = useMemo(() => {
    if (!profile || !sessions || !phaseStartDate || disabled) return null
    if (isCalibrationComplete(profile)) return null
    return buildCalibrationCalendar(profile, phaseStartDate, sessions)
  }, [profile, sessions, phaseStartDate, disabled])

  const persist = useCallback(
    (nextLayout: CalibrationCalendarLayout) => {
      writePersisted(userId, toPersisted(nextLayout))
      setSessions(nextLayout.sessions)
    },
    [userId],
  )

  const completeSession = useCallback(
    (sessionNumber: 1 | 2 | 3 | 4, completedDate: string) => {
      if (!profile || !phaseStartDate) return
      const nextSessions = markSessionCompleted(sessions ?? [], sessionNumber, completedDate)
      const nextLayout = buildCalibrationCalendar(profile, phaseStartDate, nextSessions)
      persist(nextLayout)
    },
    [profile, phaseStartDate, sessions, persist],
  )

  const reportMissedDay = useCallback(
    (missedDate: string) => {
      if (!profile || !phaseStartDate) return
      const nextSessions = rolloverMissedSession(profile, phaseStartDate, sessions ?? [], missedDate)
      const nextLayout = buildCalibrationCalendar(profile, phaseStartDate, nextSessions)
      persist(nextLayout)
    },
    [profile, phaseStartDate, sessions, persist],
  )

  const entryForDate = useCallback(
    (isoDate: string) => layout?.entries.find((e) => e.date === isoDate) ?? null,
    [layout],
  )

  const isComplete = layout?.isPhaseComplete ?? (profile ? isCalibrationComplete(profile) : false)
  const isActive = Boolean(layout && !isComplete)

  return {
    layout,
    entries: layout?.entries ?? [],
    sessions: layout?.sessions ?? [],
    isActive,
    isComplete,
    nextPendingSession: layout?.nextPendingSession ?? null,
    entryForDate,
    completeSession,
    reportMissedDay,
    refresh: hydrate,
  }
}

/** Deep-link targets for calibration feature cards surfaced on rest / meal-prep days. */
export const CALIBRATION_FEATURE_ROUTES = {
  readiness_check: '/coach/readiness',
  grocery_matcher: '/nutrition/grocery-match',
} as const
